import axios from "axios";
import { Check, ChevronRight, ImagePlus, Search, Sparkles, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { miniaturaDoProduto } from "@/utils/miniatura";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const ehPronta = (url) => String(url || "").startsWith("/uploads/padrao/");

function CartaoFoto({ foto, escolhida, onClick }) {
  return (
    <button type="button" onClick={onClick} data-e2e="foto-pronta-opcao"
      className={`group relative flex flex-col rounded-2xl border-2 overflow-hidden text-left transition-all ${escolhida ? "border-orange-500 ring-2 ring-orange-500/20" : "border-border/60 hover:border-orange-400 hover:shadow-md"}`}>
      <span className="aspect-square bg-white grid place-items-center p-2">
        <img src={miniaturaDoProduto(foto.url, 256)} alt={foto.nome} loading="lazy" decoding="async"
          className="w-full h-full object-contain transition-transform group-hover:scale-105" />
      </span>
      <span className="px-2 py-1.5 text-[11px] font-medium leading-tight line-clamp-2 min-h-[2.4rem] bg-card">{foto.nome}</span>
      {escolhida && <span className="absolute top-1.5 right-1.5 rounded-full bg-orange-500 p-1"><Check className="w-3 h-3 text-white" /></span>}
    </button>
  );
}

/** A biblioteca inteira: busca, filtro por categoria e cartões grandes. */
function Biblioteca({ open, onClose, inicial, atual, onEscolher, onEnviarPropria }) {
  const [q, setQ] = useState(inicial);
  const [fotos, setFotos] = useState(null);
  const [categoria, setCategoria] = useState(null);
  useEffect(() => { if (open) { setQ(inicial); setCategoria(null); } }, [open, inicial]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      axios.get("/api/fotos-padrao", { ...auth(), params: q.trim() ? { q } : {} })
        .then((r) => setFotos(r.data || [])).catch(() => setFotos([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);
  const categorias = useMemo(() => [...new Set((fotos || []).map((f) => f.categoria || "Outros"))], [fotos]);
  const lista = (fotos || []).filter((f) => !categoria || (f.categoria || "Outros") === categoria);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent hideClose aria-describedby={undefined} data-e2e="fotos-prontas-biblioteca"
        className="p-0 gap-0 overflow-hidden sm:max-w-2xl w-[94vw] max-h-[88vh] flex flex-col rounded-2xl">
        <div className="p-4 sm:p-5 border-b bg-gradient-to-br from-orange-500/10 to-amber-500/5">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white grid place-items-center shrink-0"><Sparkles className="w-5 h-5" /></span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg font-bold leading-tight">Fotos prontas do Núcleo</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Bebidas e produtos que são iguais em todo lugar. Toque numa foto e ela vira a foto do produto — sem fotografar nada.</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar" className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar (ex.: coca lata 350)" autoFocus
              className="w-full h-11 pl-9 pr-3 rounded-xl border bg-background text-sm outline-none focus:ring-2 focus:ring-orange-500/30" />
          </div>
          {categorias.length > 1 && (
            <div className="flex gap-1.5 mt-2.5 overflow-x-auto">
              {[null, ...categorias].map((c) => (
                <button key={c || "todas"} type="button" onClick={() => setCategoria(c)}
                  className={`h-7 px-3 rounded-full text-xs font-semibold border shrink-0 ${categoria === c ? "bg-orange-500 text-white border-orange-500" : "bg-background hover:border-orange-400"}`}>
                  {c || "Todas"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {fotos === null && <p className="text-sm text-muted-foreground text-center py-8">Carregando…</p>}
          {fotos !== null && !lista.length && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-muted-foreground">Ainda não temos foto pronta com esse nome.</p>
              {/* Só troca a foto DESTE produto: a biblioteca é só do Núcleo (rotas /admin) */}
              <button type="button" onClick={() => { onClose(); onEnviarPropria(); }}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600">
                <Upload className="w-4 h-4" /> Usar uma foto minha neste produto
              </button>
              <p className="text-[11px] text-muted-foreground">As fotos prontas são escolhidas pela equipe do Núcleo.</p>
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {lista.map((f) => (
              <CartaoFoto key={f.id} foto={f} escolhida={atual === f.url} onClick={() => { onEscolher(f.url); onClose(); }} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Fotos prontas no produto (repaginado em 24/10/2026, dono: "a pessoa não vai
 * entender"). Abaixo da foto:
 * - sem foto: um cartão claro "Usar uma foto pronta do Núcleo" que abre a
 *   biblioteca; quando o nome combina, ele já mostra as fotos sugeridas;
 * - produto NOVO cujo nome tem uma foto certeira (mesma bebida e tamanho): a
 *   foto entra sozinha, com o aviso e o "Não é essa" (o servidor faz o mesmo
 *   ao salvar, `fotos_padrao.foto_automatica`);
 * - com foto pronta: mostra que é pronta e deixa trocar.
 */
export default function FotosProntas({ nome, fotoAtual, temArquivo, onEscolher, novo = false, onEnviarPropria }) {
  const [sugestoes, setSugestoes] = useState([]);
  const [aberta, setAberta] = useState(false);
  const [automatica, setAutomatica] = useState(null); // nome da foto colocada sozinha
  const recusadas = useRef(new Set()); // "Não é essa": não coloca de novo
  const pronta = ehPronta(fotoAtual);
  const procurar = !temArquivo && (!fotoAtual || pronta);
  const termo = String(nome || "").trim();

  useEffect(() => {
    if (!procurar || termo.length < 3) { setSugestoes([]); return undefined; }
    const t = setTimeout(() => {
      axios.get("/api/fotos-padrao/sugestoes", { ...auth(), params: { nome: termo } })
        .then((r) => setSugestoes(r.data || [])).catch(() => setSugestoes([]));
    }, 400);
    return () => clearTimeout(t);
  }, [procurar, termo]);

  // Produto novo sem foto: a certeira entra sozinha
  useEffect(() => {
    if (!novo || fotoAtual || temArquivo) return;
    const certa = sugestoes.find((f) => f.certeira && !recusadas.current.has(f.url));
    if (certa) { onEscolher(certa.url); setAutomatica(certa.nome); }
  }, [novo, fotoAtual, temArquivo, sugestoes, onEscolher]);
  useEffect(() => { if (!pronta) setAutomatica(null); }, [pronta]);

  if (!procurar) return null;
  const escolhida = sugestoes.find((f) => f.url === fotoAtual);
  const naoEEssa = () => { recusadas.current.add(fotoAtual); setAutomatica(null); onEscolher(""); };

  return (
    <div className="mt-2 space-y-2" data-e2e="fotos-prontas">
      {pronta && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="min-w-0 flex-1 leading-snug">
            {automatica ? <>Colocamos a foto pronta de <b>{automatica}</b>.</> : <>Foto pronta do Núcleo{escolhida ? <> · <b>{escolhida.nome}</b></> : null}.</>}
          </span>
          {automatica
            ? <button type="button" onClick={naoEEssa} className="font-semibold text-emerald-700 dark:text-emerald-300 hover:underline shrink-0">Não é essa</button>
            : <button type="button" onClick={() => setAberta(true)} className="font-semibold text-emerald-700 dark:text-emerald-300 hover:underline shrink-0">Trocar</button>}
        </div>
      )}

      {!fotoAtual && (
        <div className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-3">
          <button type="button" onClick={() => setAberta(true)} className="w-full flex items-center gap-3 text-left" data-e2e="fotos-prontas-abrir">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white grid place-items-center shrink-0"><ImagePlus className="w-5 h-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold leading-tight">Usar uma foto pronta</span>
              <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">
                {sugestoes.length ? `Achamos ${sugestoes.length === 1 ? "1 foto" : `${Math.min(sugestoes.length, 4)} fotos`} para “${termo}”` : "Bebidas iguais em todo lugar: escolha e pronto"}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
          {sugestoes.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2.5">
              {sugestoes.slice(0, 4).map((f) => (
                <button key={f.id} type="button" onClick={() => onEscolher(f.url)} title={`Usar: ${f.nome}`}
                  className="aspect-square rounded-xl border-2 border-border/60 bg-white overflow-hidden p-1 hover:border-orange-400 transition-colors">
                  <img src={miniaturaDoProduto(f.url, 128)} alt={f.nome} loading="lazy" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Biblioteca open={aberta} onClose={() => setAberta(false)} inicial={termo} atual={fotoAtual} onEscolher={onEscolher}
        onEnviarPropria={() => onEnviarPropria?.()} />
    </div>
  );
}
